import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { i18n } from '@lingui/core'
import json2mq from 'json2mq'
import styled from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { MediaQuery } from '@/@bothrs-ui/web/components/media-query/MediaQuery'
import { AppHeaderAction } from '@/@bothrs-ui/web/components/navigation/header/AppHeaderAction'
import { useEdgeInsets } from '@/@bothrs-ui/web/hooks'
import { breakpoints } from '@/config/layout'
import { globalDefinitions } from '@/defined-translations'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'

import type { FC, ReactNode } from 'react'

export type Props = {
  /**
   * Header background image url (in case of articles)
   */
  bannerUrl?: string

  /**
   * Content to be displayed
   */
  children?: ReactNode

  /**
   * Custom banner component rendered inside of banner
   */
  bannerContent?: ReactNode

  /**
   * Whether the content is scrollable or not
   */
  scrollable?: boolean

  /**
   * Loading state when fetching data
   */
  loading?: boolean

  /**
   * Footer content
   */
  footer?: ReactNode

  onGoBack?: () => void

  preview?: boolean
}

/**
 * Maximum width for the content container
 */
const MAX_WIDTH_CONTENT = 640
/**
 * Floating CTA offset - used to calculate whether floating CTA should be displayed (only on desktop)
 */
const FLOATING_CTA_OFFSET = 125

/**
 * Media query for desktop
 */
const DESKTOP_MEDIA_QUERY = MAX_WIDTH_CONTENT + FLOATING_CTA_OFFSET

export const ContentItemDetailLayout: FC<Props> = ({
  bannerUrl,
  bannerContent,
  children,
  loading,
  scrollable = true,
  footer,
  preview,
  onGoBack,
}) => {
  const { top } = useEdgeInsets()

  const handleGoBack = () => {
    onGoBack?.()
  }

  if (loading) {
    return (
      <StLoadingContainer>
        <AnimatedLoader />
      </StLoadingContainer>
    )
  }

  return (
    <StContainer scrollable={scrollable} preview={preview}>
      <MediaQuery query={json2mq({ minWidth: DESKTOP_MEDIA_QUERY })}>
        {onGoBack ? (
          <CloseContainer onClick={handleGoBack}>
            <FontAwesomeIcon color='black' icon={['far', 'arrow-left']} />
          </CloseContainer>
        ) : null}
      </MediaQuery>

      <StContentContainer preview={preview}>
        {/* Header backgorund image + back button */}
        <StBanner bannerUrl={bannerUrl}>
          {bannerContent ? <StBannerOverlay /> : null}
          <MediaQuery query={json2mq({ maxWidth: DESKTOP_MEDIA_QUERY })}>
            <StBannerActionContainer top={top}>
              <AppHeaderAction
                key='app-header-action'
                action={{
                  label: i18n._(globalDefinitions.goBack),
                  icon: ['far', 'chevron-left'],
                  onClick: handleGoBack,
                }}
                color='white'
              />
            </StBannerActionContainer>
          </MediaQuery>
          {bannerContent ? <StBannerContent>{bannerContent}</StBannerContent> : null}
        </StBanner>

        {/* Content */}
        <StContent>{children}</StContent>

        {/* Footer */}
      </StContentContainer>
      {footer ? (
        <StFooter>
          <StFooterContent>{footer}</StFooterContent>
        </StFooter>
      ) : null}
    </StContainer>
  )
}

const StContainer = styled(FlexColumn)<{
  scrollable?: boolean
  preview?: boolean
}>`
  height: 100%;
  ${({ scrollable }) => (scrollable ? 'overflow-y: auto;' : 'overflow: hidden;')}
  position: relative;

  padding-top: ${({ theme, preview }) => (preview ? 0 : theme.UI.SpacingPx.Space12)};
  @media only screen and (max-width: ${DESKTOP_MEDIA_QUERY}px) {
    padding-top: 0;
  }
`

const StBanner = styled.div<{ bannerUrl?: string }>`
  ${({ bannerUrl }) =>
    bannerUrl &&
    `
    background-image: url(${encodeURI(bannerUrl)});
`}

  width: 100%;
  max-width: ${MAX_WIDTH_CONTENT}px;
  flex-shrink: 0;
  aspect-ratio: 16 / 9 !important;

  @media only screen and (max-width: ${DESKTOP_MEDIA_QUERY}px) {
    border-radius: 0 0 80px 0 !important;
    overflow: hidden;
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        to bottom right,
        rgba(0, 0, 0, 0.5) 0%,
        /* Very dark at top-left */ rgba(0, 0, 0, 0) 100%
          /* Fully transparent at bottom-right */
      );
      pointer-events: none; /* Ensures it doesn't interfere with interactions */
      z-index: 1;
    }
  }

  border-radius: ${({ theme }) => theme.UI.BorderRadius.Large}px;

  background-size: cover;
  background-position: center;
  background-color: rgba(0, 0, 0, 0.5);
  overflow: hidden;
  position: relative;

  display: flex;
  flex-direction: column;
`

const StBannerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;

  z-index: 1;

  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.5) 100%
  );
`

const StBannerActionContainer = styled.div<{ top?: number }>`
  margin-top: ${(props) => `${props.top}px` || '0'};
  padding: ${({ theme }) => `${theme.UI.SpacingPx.Space8} ${theme.UI.SpacingPx.Space6}`};

  position: absolute;
  top: 0;
  left: 0;
  height: 120px;

  z-index: 101;
`

const StBannerContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  z-index: 100;
`

const CloseContainer = styled.div`
  top: ${({ theme }) => theme.UI.SpacingPx.Space12};

  // On desktop, we use a percentage position because of the available space
  left: 7.5%;

  // On mobile, we use a fixed position because of the amount of available space
  @media ${breakpoints.toS} {
    left: ${({ theme }) => theme.UI.SpacingPx.Space4};
  }

  @media ${breakpoints.toM} {
    left: ${({ theme }) => theme.UI.SpacingPx.Space6};
  }

  z-index: 100;
  color: white;
  cursor: pointer;

  position: absolute;
`

const StContentContainer = styled(FlexColumn)<{ preview?: boolean }>`
  ${({ theme, preview }) =>
    preview
      ? `padding: ${theme.UI.SpacingPx.Space10}; padding-bottom: ${theme.UI.SpacingPx.Space20}; max-width: ${
          MAX_WIDTH_CONTENT + theme.UI.Spacing.Space20
        }px; margin: 0 auto;`
      : `max-width: ${MAX_WIDTH_CONTENT}px; margin: 0 auto;`}
  overflow-y: auto;
  width: 100%;
  flex: 1;
`

const StContent = styled(FlexColumn)`
  padding: ${({ theme }) => `${theme.UI.SpacingPx.Space2} ${theme.UI.SpacingPx.Space6}`};

  flex-grow: 1;

  @media only screen and (min-width: ${DESKTOP_MEDIA_QUERY}px) {
    padding: 0;
  }
`

const StFooter = styled.footer`
  flex-shrink: 1;

  display: flex;
  justify-content: center;
  align-items: center;

  box-shadow: 0px -1px 20px rgba(4, 24, 49, 0.07);

  padding: ${({ theme }) => theme.UI.SpacingPx.Space3}
    ${({ theme }) => theme.UI.SpacingPx.Space6}
    calc(
      ${({ theme }) => theme.UI.SpacingPx.Space3} +
        max(var(--safe-area-inset-bottom, 0px), 20px)
    )
    ${({ theme }) => theme.UI.SpacingPx.Space6};
`

const StFooterContent = styled.div`
  max-width: ${MAX_WIDTH_CONTENT}px;
  width: 100%;
`

const StLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`
